import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  Box, Button,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../components/DropdownMenu';
import FormInput from '../../components/form/FormInput';
import { inputTypes } from '../../components/form/FormInput/util';
import InputLabel from '../../components/form/InputLabel';
import { UserIcon } from '../../components/library';
import SearchInputGroup from '../../components/SearchInputGroup';
import { hideModal } from '../../redux/modules/modal';
import { useCompanyUsers, useUpdateCompany } from '../../services/company-service';
import { menuItemBaseStyle } from '../../styles/theme/components/menu';
import colors from '../../styles/theme/foundation/colors';
import { removePhoneNumberFormat } from '../../utils/formatPhoneUtil';
import { getUserFullName } from '../../utils/userNameUtil';

import { recordTierOptions } from './util';

const EditCompanyModal = ({ company }) => {
  const dispatch = useDispatch();
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [searchTerm, setSearchTerm] = useState('');
  const [recordTier, setRecordTier] = useState(company.recordTier ?? 'none');
  const [selectedCompanyContactId, setSelectedCompanyContactId] = useState(null);
  const { data: users, isLoading: usersLoading } = useCompanyUsers(company.companyId);

  const { mutate } = useUpdateCompany({ companyId: company.companyId });

  const useFormMethods = useForm({
    defaultValues: {
      company_name: company?.companyName,
      short_name: company?.shortName,
      company_address_1: company?.companyAddress1,
      company_address_2: company?.companyAddress2,
      company_city: company?.companyCity,
      company_state: company?.companyState,
      company_zip: company?.companyZip,
      company_phone: company?.companyPhone,
      company_fax: company?.companyFax,
      company_contact: company?.companyContact,
      company_alias: company?.companyAlias,
      record_tier: company?.recordTier
    }
  });

  const { handleSubmit } = useFormMethods;

  const companyContact = useMemo(() => {
    if (usersLoading) {
      return null;
    }

    if (selectedCompanyContactId) {
      return users.find(u => u.userId === selectedCompanyContactId);
    }

    return users.find(u => u.userId === company.companyContact);
  }, [users, usersLoading, selectedCompanyContactId]);

  const userList = useMemo(() => {
    if (usersLoading) {
      return [];
    }

    if (searchTerm === '' || searchTerm === undefined) {
      return users;
    }

    const searchValue = searchTerm.toLowerCase();

    if (Array.isArray(users)) {
      return users.filter(i => {
        const userFullName = getUserFullName(i.userFirstName, i.userLastName);

        return userFullName.toLowerCase().includes(searchValue);
      });
    }

    return users ?? [];
  }, [users, searchTerm]);

  const onSubmit = data => {
    mutate({
      company_name: data.company_name,
      short_name: data.short_name,
      company_address_1: data.company_address_1,
      company_address_2: data.company_address_2,
      company_city: data.company_city,
      company_state: data.company_state,
      company_zip: data.company_zip,
      company_phone: removePhoneNumberFormat(data.company_phone),
      company_fax: removePhoneNumberFormat(data.company_fax),
      company_contact: companyContact?.userId ?? selectedCompanyContactId ?? company.companyContact ?? null,
      company_alias: data.company_alias,
      record_tier: recordTier ?? company?.recordTier ?? null
    });
  };

  if (usersLoading || !companyContact) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <HStack
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={4}
        >
          <Box flexBasis="50%">
            <FormInput
              inputName="company_name"
              labelName="Company Name"
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="short_name"
              labelName="Short Name"
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="company_address_1"
              labelName="Company Address 1"
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="company_address_2"
              labelName="Company Address 2"
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="company_city"
              labelName="Company City"
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="company_state"
              labelName="Company State"
              useFormMethods={useFormMethods}
            />
          </Box>

          <Box flexBasis="50%">
            <FormInput
              inputName="company_zip"
              labelName="Company Zip"
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="company_phone"
              labelName="Company Phone"
              type={inputTypes.phoneNumber}
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="company_fax"
              labelName="Company Fax"
              type={inputTypes.phoneNumber}
              useFormMethods={useFormMethods}
            />
            <FormInput
              inputName="company_alias"
              labelName="Company Alias"
              useFormMethods={useFormMethods}
            />

            <Box mb={6}>
              <InputLabel labelName="Company Contact" />
              <Popover
                isOpen={isOpen}
                onClose={onClose}
                onOpen={onOpen}
              >
                <PopoverTrigger>
                  <HStack justifyContent="space-between" mb={2}>
                    <HStack cursor="pointer" maxW="85%">
                      <UserIcon
                        firstName={companyContact?.userFirstName ?? '-'}
                        lastName={companyContact?.userLastName ?? '-'}
                      />
                      <Text noOfLines={1} textStyle="bodySm">{`${companyContact?.userFirstName ?? '-'} ${companyContact?.userLastName ?? '-'}`}</Text>
                    </HStack>
                  </HStack>
                </PopoverTrigger>
                <PopoverContent
                  zIndex="2000"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <PopoverArrow />
                  <PopoverBody maxW="300px" p={3}>
                    <SearchInputGroup
                      dataAnalyticsId="create-watcher-search-input"
                      isDisabled={false}
                      mb={4}
                      searchTerm={searchTerm}
                      variant="primary"
                      width="100%"
                      onChange={e => {
                        setSearchTerm(e.target.value);
                      }}
                    />
                    {!!userList.length && (
                      <Box maxH="160px" overflowY="auto">
                        {userList.map(u => (
                          <HStack
                            _hover={{
                              cursor: 'pointer',
                              bgColor: colors.primaryScale['50'],
                              color: colors.primaryScale['600']
                            }}
                            _selected={menuItemBaseStyle._focus} //eslint-disable-line
                            height="40px"
                            key={u.userId}
                            py="10px"
                            onClick={() => {
                              setSearchTerm('');
                              onClose();
                              setSelectedCompanyContactId(u.userId);
                            }}
                          >
                            <UserIcon
                              firstName={u.userFirstName}
                              lastName={u.userLastName}
                            />
                            <Text noOfLines={1} textStyle="bodySm">{`${u.userFirstName} ${u.userLastName}`}</Text>
                          </HStack>
                        ))}
                      </Box>
                    )}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Box>

            <Box>
              <InputLabel labelName="Record Tier" />

              <DropdownMenu
                options={recordTierOptions}
                selected={recordTier}
                variant="secondary"
                onSelect={e => setRecordTier(e.value)}
              />
            </Box>
          </Box>
        </HStack>

        <HStack justifyContent="center" spacing={4}>
          <Button variant="secondary" onClick={() => dispatch(hideModal())}>
            Cancel
          </Button>

          <Button type="submit">
            Save
          </Button>
        </HStack>
      </form>
    </Box>
  );
};

EditCompanyModal.propTypes = { company: PropTypes.object.isRequired };

export default EditCompanyModal;
